'use client';

import type { FC } from 'react';
import { ScreenContent, ScreenFixedTop, ScreenTabBar } from '../../screen';
import { useNavigation } from '../../contexts/NavigationContext';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { useProfileData } from '../../queries/useProfileQueries';
import { useRushMidConfig } from '../../queries/useRushMidConfig';
import type { ScreenParams } from '../../types/navigation';
import rushMidHero from '../../assets/games/rush-mid-hero.webp';
import rouletteHero from '../../assets/games/roulette-hero.webp';
import caseBattleHero from '../../assets/games/case-battles-hero.webp';
import contractsHero from '../../assets/games/contracts-hero.webp';
import upgradeHero from '../../assets/games/upgrade-hero.webp';
import shopHero from '../../assets/games/shop-hero.webp';
import translations from './GamesScreen.translations';

interface GamesScreenProps {
  params?: ScreenParams;
  onBack?: () => void;
  isPreview?: boolean;
}

export const GamesScreen: FC<GamesScreenProps> = () => {
  const { t } = useLocalTranslation(translations);
  const navigation = useNavigation();
  const profile = useProfileData();
  const rushMid = useRushMidConfig();

  const games = [
    {
      id: 'rush-mid',
      title: t('rushMidTitle'),
      // teased mode keeps its name but hides what it is — the description is literally ???
      desc: rushMid.comingSoon ? '???' : t('rushMidDescription'),
      cta: t('rushMidCta'),
      hero: rushMidHero.src,
      accent: '#ef4444',
      screen: 'rush-mid' as const,
      // teased OR draining — either way the tile doesn't navigate; the badge says which
      active: !rushMid.comingSoon && !rushMid.maintenance,
      comingSoon: rushMid.comingSoon,
      maintenance: !rushMid.comingSoon && rushMid.maintenance,
    },
    {
      id: 'roulette',
      title: t('rouletteTitle'),
      // restricted → non-exchange users see «Скоро» + the name/description, no CTA (same as Case Battles)
      desc: t('rouletteDescription'),
      cta: t('rouletteCta'),
      hero: rouletteHero.src,
      accent: '#dc2626',
      screen: 'roulette' as const,
      active: profile?.rouletteRestrictedMode ? (profile?.skinsExchangeEnabled ?? false) : true,
      comingSoon: profile?.rouletteRestrictedMode ? !profile?.skinsExchangeEnabled : false,
      maintenance: false,
    },
    {
      id: 'case-battles',
      title: t('caseBattlesTitle'),
      desc: t('caseBattlesDescription'),
      cta: t('caseBattlesCta'),
      hero: caseBattleHero.src,
      accent: '#3b82f6',
      screen: 'case-battles' as const,
      active: profile?.caseBattlesRestrictedMode ? (profile?.skinsExchangeEnabled ?? false) : true,
      comingSoon: profile?.caseBattlesRestrictedMode ? !profile?.skinsExchangeEnabled : false,
      maintenance: false,
    },
    {
      id: 'contracts',
      title: t('contractsTitle'),
      desc: t('contractsDescription'),
      cta: t('contractsCta'),
      hero: contractsHero.src,
      accent: '#8b5cf6',
      screen: 'contracts' as const,
      active: true,
      comingSoon: false,
      maintenance: false,
    },
    {
      id: 'upgrade',
      title: t('upgradeTitle'),
      desc: t('upgradeDescription'),
      cta: t('upgradeCta'),
      hero: upgradeHero.src,
      accent: '#22c55e',
      screen: 'upgrade' as const,
      active: true,
      comingSoon: false,
      maintenance: false,
    },
    {
      id: 'shop',
      title: t('shopTitle'),
      desc: t('shopDescription'),
      cta: t('shopCta'),
      hero: shopHero.src,
      accent: '#f59e0b',
      screen: 'shop' as const,
      active: true,
      comingSoon: false,
      maintenance: false,
    },
  ];

  return (
    <>
      <ScreenFixedTop title={t('title')} />
      <ScreenTabBar />
      <ScreenContent scrollKey="games">
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            paddingTop: 16,
            paddingLeft: 16,
            paddingRight: 16,
            paddingBottom: 'calc(72px + 16px + var(--tg-viewport-safe-area-inset-bottom, 0px))',
          }}
        >
          {games.map((game) => (
            <div
              key={game.id}
              onClick={game.active ? () => navigation.pushScreen('games', game.screen) : undefined}
              style={{
                position: 'relative',
                height: 150,
                borderRadius: 22,
                overflow: 'hidden',
                border: '1px solid rgba(255,255,255,0.08)',
                cursor: game.active ? 'pointer' : 'default',
                opacity: game.comingSoon || game.maintenance ? 0.75 : 1,
              }}
            >
              <img
                src={game.hero}
                alt={game.title}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
              />
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(90deg, rgba(7,7,12,0.94) 0%, rgba(7,7,12,0.62) 46%, transparent 100%)',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  left: 18,
                  top: 0,
                  bottom: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  paddingTop: 12,
                  paddingBottom: 12,
                  maxWidth: '66%',
                }}
              >
                {(game.comingSoon || game.maintenance) && (
                  <span
                    style={{
                      alignSelf: 'flex-start',
                      marginBottom: 8,
                      fontSize: 10,
                      fontWeight: 700,
                      color: '#fbbf24',
                      background: 'rgba(251,191,36,0.12)',
                      border: '1px solid rgba(251,191,36,0.35)',
                      padding: '2px 8px',
                      borderRadius: 9999,
                    }}
                  >
                    {game.comingSoon ? t('comingSoon') : t('maintenanceBadge')}
                  </span>
                )}
                <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.01em', color: '#fff' }}>{game.title}</div>
                <div style={{ fontSize: 13, color: '#fff', marginTop: 4 }}>{game.desc}</div>
                {!game.comingSoon && !game.maintenance && (
                  <button
                    type="button"
                    style={{
                      alignSelf: 'flex-start',
                      // прижимает кнопку к низу плитки: свободное место уходит в auto-отступ,
                      // и кнопки соседних плиток стоят в ряд независимо от числа строк подписи
                      marginTop: 'auto',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: 13,
                      fontWeight: 700,
                      color: '#fff',
                      background: game.accent,
                      padding: '7px 18px',
                      borderRadius: 9999,
                    }}
                  >
                    {game.cta}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </ScreenContent>
    </>
  );
};
